import { formatBps, formatDay, formatMoney } from '@bemor/web-kit';
import {
    Badge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type {
    RiskBacktestResults,
    RiskCurve,
    RiskRuleStatsRow,
    RiskRuleStatsTotals,
} from '@/api/types';

/* BM-125 slice 5: the rule performance drawer (decision 12), the backtest results and the appetite curve (decision
   13), the model state (decision 45). Same recharts set and colour tokens as dashboard-charts.tsx; figures are the
   server's, nothing is summed in the browser. */

const C1 = 'var(--chart-1, var(--fg-1))';
const C2 = 'var(--chart-2, var(--signal-500))';
const C3 = 'var(--chart-3, var(--gray-400))';
const DANGER = 'var(--danger-solid)';

function Empty({ text }: { text: string }) {
    return <p className="text-fg-2 m-0 py-8 text-center text-sm">{text}</p>;
}

/** A basis-point share as a percentage, "—" when the server has no denominator. */
export const shareLabel = (bps: number | null | undefined): string =>
    bps == null ? '—' : formatBps(bps);

/** The window's totals in one line for the drawer head: hits, declines (share), estimate, cost. */
export function totalsSummary(t: RiskRuleStatsTotals): string {
    const parts = [
        `${t.hits} hits`,
        `${t.declines} declines (${shareLabel(t.decline_share_bps)} of attempts)`,
        `${t.reviews} reviews`,
    ];
    if (t.declines > 0)
        parts.push(
            `${t.false_positive_estimate} look wrong (${shareLabel(t.false_positive_bps)})`,
        );
    if (t.conversion_cost_cents > 0)
        parts.push(
            `${formatMoney(t.conversion_cost_cents)} declined without a fraud label`,
        );
    return parts.join(' · ');
}

/** The daily hits, declines and reviews of one rule, oldest first. */
export function RuleStatsChart({
    rows,
}: {
    rows: readonly RiskRuleStatsRow[];
}) {
    if (rows.length === 0)
        return <Empty text="No checks hit this rule in the last 90 days." />;
    const data = [...rows].reverse().map((r) => ({
        day: r.day,
        hits: r.hits,
        shadow: r.shadow_hits,
        declines: r.declines,
        reviews: r.reviews,
    }));
    return (
        <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="day"
                    tickFormatter={(d: string) => formatDay(d)}
                />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={(d) => formatDay(String(d))} />
                <Legend />
                <Line
                    type="monotone"
                    dataKey="hits"
                    name="Hits"
                    stroke={C1}
                    dot={false}
                />
                <Line
                    type="monotone"
                    dataKey="shadow"
                    name="Shadow hits"
                    stroke={C3}
                    dot={false}
                />
                <Line
                    type="monotone"
                    dataKey="declines"
                    name="Declines"
                    stroke={DANGER}
                    dot={false}
                />
                <Line
                    type="monotone"
                    dataKey="reviews"
                    name="Reviews"
                    stroke={C2}
                    dot={false}
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

/** The later labels of the rule's hits per day: fraud and good. */
export function RuleLabelsChart({
    rows,
}: {
    rows: readonly RiskRuleStatsRow[];
}) {
    if (rows.length === 0) return null;
    const data = [...rows].reverse().map((r) => ({
        day: r.day,
        fraud: r.labelled_fraud,
        good: r.labelled_good,
    }));
    return (
        <ResponsiveContainer width="100%" height={160}>
            <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="day"
                    tickFormatter={(d: string) => formatDay(d)}
                />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={(d) => formatDay(String(d))} />
                <Legend />
                <Bar
                    dataKey="fraud"
                    name="Later labelled fraud"
                    fill={DANGER}
                />
                <Bar dataKey="good" name="Later labelled good" fill={C3} />
            </BarChart>
        </ResponsiveContainer>
    );
}

/** The appetite curve: share declined against fraud caught for the three presets, the current one marked. */
export function CurveChart({ curve }: { curve: RiskCurve }) {
    if (curve.points.length === 0 || (curve.checks ?? 0) === 0)
        return (
            <Empty text="No labelled checks in the last 90 days yet: the curve needs outcomes." />
        );
    const data = curve.points.map((p) => ({
        appetite: p.appetite,
        declined: p.declined_bps,
        reviewed: p.reviewed_bps,
        caught: p.fraud_caught_bps,
        goodDeclined: p.good_declined_bps,
    }));
    return (
        <div className="space-y-2" data-testid="risk-curve">
            <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="appetite" />
                    <YAxis tickFormatter={(v: number) => formatBps(v)} />
                    <Tooltip formatter={(v) => formatBps(Number(v))} />
                    <Legend />
                    <Bar dataKey="declined" name="Declined" fill={DANGER} />
                    <Bar dataKey="reviewed" name="Reviewed" fill={C2} />
                    <Bar dataKey="caught" name="Fraud caught" fill={C1} />
                    <Bar
                        dataKey="goodDeclined"
                        name="Good declined"
                        fill={C3}
                    />
                </BarChart>
            </ResponsiveContainer>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Appetite</TableHead>
                        <TableHead>Bands</TableHead>
                        <TableHead className="text-right">Declined</TableHead>
                        <TableHead className="text-right">Reviewed</TableHead>
                        <TableHead className="text-right">
                            Fraud caught
                        </TableHead>
                        <TableHead className="text-right">
                            Good declined
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {curve.points.map((p) => (
                        <TableRow
                            key={p.appetite}
                            data-current={String(p.appetite === curve.appetite)}
                        >
                            <TableCell>
                                {p.appetite}{' '}
                                {p.appetite === curve.appetite ? (
                                    <Badge variant="outline">current</Badge>
                                ) : null}
                            </TableCell>
                            <TableCell className="tabular-nums">
                                {p.bands.three_ds} / {p.bands.review} /{' '}
                                {p.bands.decline}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {formatBps(p.declined_bps)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {formatBps(p.reviewed_bps)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {formatBps(p.fraud_caught_bps)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {formatBps(p.good_declined_bps)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <p className="text-fg-2 m-0 text-xs">
                {curve.checks} labelled checks of the last {curve.window_days}{' '}
                days, replayed under each preset's bands.
            </p>
        </div>
    );
}

/** The results of a finished backtest: the shares, the catch, the hits per rule. */
export function BacktestResultsView({
    results,
}: {
    results: RiskBacktestResults;
}) {
    const r = results;
    const lines: [string, string][] = [
        ['Checks replayed', String(r.checks)],
        ['Labelled fraud / good', `${r.labelled_fraud} / ${r.labelled_good}`],
        ['Declined', `${r.declined} (${formatBps(r.declined_bps)})`],
        ['Reviewed', `${r.reviewed} (${formatBps(r.reviewed_bps)})`],
        [
            'Fraud caught',
            `${r.fraud_caught} of ${r.labelled_fraud} (${formatBps(r.fraud_caught_bps)})`,
        ],
        [
            'Good declined',
            `${r.good_declined} of ${r.labelled_good} (${formatBps(r.good_declined_bps)})`,
        ],
        [
            `Precision at ${r.queue_capacity_per_day} a day`,
            r.precision_at_capacity_bps == null
                ? '—'
                : formatBps(r.precision_at_capacity_bps),
        ],
    ];
    return (
        <div className="space-y-3" data-testid="backtest-results">
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm md:grid-cols-2">
                {lines.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3">
                        <dt className="text-fg-2">{k}</dt>
                        <dd className="m-0 tabular-nums">{v}</dd>
                    </div>
                ))}
            </dl>
            {r.rules.length ? (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Rule</TableHead>
                            <TableHead className="text-right">Hits</TableHead>
                            <TableHead className="text-right">
                                Shadow hits
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {r.rules.map((x) => (
                            <TableRow key={x.rule_key}>
                                <TableCell className="font-mono text-xs">
                                    {x.rule_key}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {x.hits}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {x.shadow_hits ?? 0}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : null}
        </div>
    );
}
